import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import EvaluationNavigation from '../../components/EvaluationNavigation';
import { getAllArchiveEvaluationsAsync } from '../../services/evaluationService';
import theme from '../../theme/theme';
import MySpinner from '../../components/MySpinner';
import { useNavigate } from 'react-router-dom';
import { routePath } from '../../routes/routePath';
import { ArchiveFill } from 'react-bootstrap-icons';

function ArchiveEvaluations() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFetched, setIsFetched] = useState(false);

  useEffect(() => {
    const fetchEvaluations = async () => {
      setLoading(true);
      setError(null);
      setIsFetched(false);

      try {
        const response = await getAllArchiveEvaluationsAsync();
        setEvaluations(response);
      } catch (err) {
        setError(err.message || 'حدث خطأ أثناء جلب البيانات.');
      } finally {
        setLoading(false);
        setIsFetched(true);
      }
    };

    fetchEvaluations();
  }, []);

  const handleEvaluationDetails = (empEvalId) => {
    navigate(routePath.detailsEvaluation(empEvalId));
  };

  if (loading) return <Layout><MySpinner color={theme.colors.primary} /></Layout>;

  if (error) return (
    <Layout>
      <EvaluationNavigation />
      <div className="alert alert-danger text-center my-5" style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <strong>خطأ:</strong> {error}
      </div>
    </Layout>
  );

  return (
    <Layout>
      <EvaluationNavigation />

      <div style={styles.container}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h2 style={styles.headerTitle}>
              <span style={styles.headerIcon}></span>
              التقييمات المؤرشفة
            </h2>
            {evaluations.length > 0 && (
              <span style={styles.countBadge}>
                {evaluations.length} تقييم
              </span>
            )}
          </div>
          <p style={styles.headerSubtitle}>
            سجل التقييمات السابقة للسنوات الأكاديمية
          </p>
        </div>

        {/* Empty State */}
        {!loading && isFetched && evaluations.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}><ArchiveFill/></div>
            <h3 style={styles.emptyTitle}>لا توجد تقييمات مؤرشفة</h3>
            <p style={styles.emptyText}>
              لم يتم العثور على أي تقييمات سابقة في الأرشيف
            </p>
          </div>
        )}

        {/* Evaluations Grid */}
        {!loading && isFetched && evaluations.length > 0 && (
          <div style={styles.cardsGrid}>
            {evaluations.map((evaluation) => (
              <div
                key={evaluation.empEvalId}
                onClick={() => handleEvaluationDetails(evaluation.empEvalId)}
                style={styles.card}
              >
                {/* Card Header */}
                <div style={styles.cardHeader}>
                  <div style={styles.yearBadge}>
                    <span style={styles.yearIcon}>📅</span>
                    {evaluation.academicYear}
                  </div>
                  <span style={styles.archiveBadge}>مؤرشف</span>
                </div>

                {/* Card Body */}
                <div style={styles.cardBody}>
                  <h3 style={styles.employeeName}>{evaluation.subordinateName}</h3>

                  <div style={styles.infoRow}>
                    <span style={styles.infoIcon}>🏢</span>
                    <div style={styles.infoContent}>
                      <span style={styles.infoLabel}>القسم</span>
                      <span style={styles.infoValue}>{evaluation.deptName}</span>
                    </div>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoIcon}>💼</span>
                    <div style={styles.infoContent}>
                      <span style={styles.infoLabel}>المسمى الوظيفي</span>
                      <span style={styles.infoValue}>{evaluation.positionTitle}</span>
                    </div>
                  </div>

                  <div style={styles.percentageBox}>
                    <span style={styles.percentageLabel}>النسبة المئوية</span>
                    <span style={styles.percentageValue}>
                      {evaluation.overallPercentage ? `${evaluation.overallPercentage}%` : 'غير متوفرة'}
                    </span>
                  </div>
                </div>

                {/* Card Footer */}
                <div style={styles.cardFooter}>
                  <span style={styles.viewDetailsText}>
                    عرض التفاصيل
                    <span style={styles.arrowIcon}>←</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
    direction: 'rtl'
  },
  header: {
    marginBottom: '2rem',
    textAlign: 'center'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '0.75rem'
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: 0
  },
  headerIcon: {
    fontSize: '2rem'
  },
  countBadge: {
    padding: '0.5rem 1.25rem',
    borderRadius: '20px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 'bold',
    fontSize: '0.95rem'
  },
  headerSubtitle: {
    fontSize: '1rem',
    color: '#666',
    margin: 0
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
    gap: '1.5rem'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #e0e0e0'
  },
  yearBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '12px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 'bold',
    fontSize: '0.95rem'
  },
  yearIcon: {
    fontSize: '1.1rem'
  },
  archiveBadge: {
    padding: '0.4rem 0.9rem',
    borderRadius: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    border: '1px solid #ddd'
  },
  cardBody: {
    padding: '1.5rem 1.25rem'
  },
  employeeName: {
    fontSize: '1.35rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '1.25rem',
    textAlign: 'center'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f0f0f0'
  },
  infoIcon: {
    fontSize: '1.25rem',
    marginTop: '0.2rem'
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    flex: 1
  },
  infoLabel: {
    fontSize: '0.85rem',
    color: '#999',
    fontWeight: '500'
  },
  infoValue: {
    fontSize: '1rem',
    color: '#1a1a1a',
    fontWeight: '600'
  },
  percentageBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1.25rem',
    padding: '1rem 1.25rem',
    backgroundColor: '#f1f8f4',
    borderRadius: '12px',
    border: '2px solid #4caf50'
  },
  percentageLabel: {
    fontSize: '0.95rem',
    color: '#2e7d32',
    fontWeight: '600'
  },
  percentageValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1b5e20'
  },
  cardFooter: {
    padding: '1rem 1.25rem',
    backgroundColor: '#fafafa',
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center'
  },
  viewDetailsText: {
    fontSize: '0.95rem',
    color: '#1565c0',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  arrowIcon: {
    fontSize: '1.1rem',
    transition: 'transform 0.3s ease'
  },
  emptyState: {
    textAlign: 'center',
    padding: '5rem 2rem',
    backgroundColor: '#fafafa',
    borderRadius: '16px',
    border: '2px dashed #e0e0e0',
    maxWidth: '600px',
    margin: '2rem auto'
  },
  emptyIcon: {
    width: '100px',
    height: '100px',
    margin: '0 auto 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontSize: '3rem'
  },
  emptyTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '0.5rem'
  },
  emptyText: {
    fontSize: '1rem',
    color: '#666',
    margin: 0
  }
};

// Add hover effects via CSS-in-JS workaround
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    [style*="cursor: pointer"]:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.15) !important;
    }
    [style*="cursor: pointer"]:hover [style*="arrowIcon"] {
      transform: translateX(-4px);
    }
  `;
  if (!document.head.querySelector('style[data-archive-hover]')) {
    styleSheet.setAttribute('data-archive-hover', 'true');
    document.head.appendChild(styleSheet);
  }
}

export default ArchiveEvaluations;